import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, useLocation } from 'react-router';

import { serpsRoutes } from '@/config';
import { useBreakpoints } from '@/hooks';

import { CITY, EMPLOYMENT_TYPE, ROLE, TRANSLATIONS_MAP } from '../../constants';
import { SerpsRouteInfo, useSerpsMatch } from '../../hooks/use-serps-match';
import { getCategoryTranslation } from '../../utils/get-category-translation';
import { capitalizeFirstLetter } from '../../utils/get-seo-serps-data';
import { employmentTypeSlugToLocalizedKey, slugify } from '../../utils/static-data-utils';

import * as S from './serps-header.styles';

export const SerpsHeader: React.VFC = () => {
  const { search } = useLocation();
  const radius = new URLSearchParams(search).get('radius');
  const { formatMessage, locale } = useIntl();
  const { isMobile } = useBreakpoints();
  const { category, categoryValue, subcategory, subcategoryValue } = useSerpsMatch() as SerpsRouteInfo;
  const hasSubcategory = subcategory && subcategoryValue;
  const isEmploymentTypeSingleCategory = !hasSubcategory && category === EMPLOYMENT_TYPE;

  const { title, paramName, subparamName } = TRANSLATIONS_MAP[hasSubcategory ? `${category}-${subcategory}` : category];

  const titleKey = isEmploymentTypeSingleCategory ? title[employmentTypeSlugToLocalizedKey(categoryValue)] : title;
  const values = {
    [paramName]: getCategoryTranslation(formatMessage, category, categoryValue),
    ...(hasSubcategory &&
      subparamName && { [subparamName]: getCategoryTranslation(formatMessage, subcategory, subcategoryValue) }),
  };

  const isLocalJobRoleSerp = category === ROLE && subcategory === CITY && hasSubcategory;
  const localJobRoleSerpsValues = isLocalJobRoleSerp
    ? {
        role: (
          <S.Link href={generatePath(serpsRoutes.role, { categoryValue: slugify(categoryValue) })}>
            {capitalizeFirstLetter(categoryValue)}
          </S.Link>
        ),
        city: (
          <S.Link href={generatePath(serpsRoutes.city, { categoryValue: slugify(subcategoryValue) })}>
            {capitalizeFirstLetter(subcategoryValue)}
          </S.Link>
        ),
      }
    : {};

  const isLocalEmploymentTypeSerp = category === EMPLOYMENT_TYPE && subcategory === CITY && hasSubcategory;
  const localEmploymentTypeSerpsValues = isLocalEmploymentTypeSerp
    ? {
        employmentType: capitalizeFirstLetter(getCategoryTranslation(formatMessage, category, categoryValue)),
        city: capitalizeFirstLetter(getCategoryTranslation(formatMessage, subcategory, subcategoryValue)),
      }
    : {};

  let titleKeyOverride = '';
  const isAushilfeEmploymentTypeSerp =
    category === EMPLOYMENT_TYPE && subcategory === ROLE && values.job_type.includes('Aushilfe');
  const aushilfeEmploymentTypeSerpValues =
    isAushilfeEmploymentTypeSerp && subcategoryValue
      ? { category: capitalizeFirstLetter(getCategoryTranslation(formatMessage, subcategory, subcategoryValue)) }
      : {};
  const isStudentEmploymentType = categoryValue === 'student' && subcategory === CITY;
  const isFreelancerEmploymentType = categoryValue === 'freiberuflich' && subcategory === CITY;

  if (isStudentEmploymentType && locale === 'de') {
    titleKeyOverride = 'XJM_SEARCH_TITLE_EMPLOYMENT_TYPE_CITY_STUDENTEN';
  }

  if (isFreelancerEmploymentType && locale === 'de') {
    titleKeyOverride = 'XJM_SEARCH_TITLE_EMPLOYMENT_TYPE_CITY_FREELANCER';
  }

  if (isAushilfeEmploymentTypeSerp && locale === 'de') {
    titleKeyOverride = 'XJM_SEARCH_TITLE_JOB_TYPE_CATEGORY_MINIJOBS';
  }

  const Header: React.FC = ({ children }) =>
    isMobile ? (
      <S.Headline size="xxlarge" forwardedAs="h1">
        {children}
      </S.Headline>
    ) : (
      <S.Hero size="small" forwardedAs="h1">
        {children}
      </S.Hero>
    );

  return (
    <Header>
      <FormattedMessage
        id={titleKeyOverride ? titleKeyOverride : titleKey}
        values={{
          ...values,
          ...localJobRoleSerpsValues,
          ...localEmploymentTypeSerpsValues,
          ...aushilfeEmploymentTypeSerpValues,
        }}
      />{' '}
      {radius ? <FormattedMessage id="XJM_SEO_RADIUS" values={{ radius }} /> : null}
    </Header>
  );
};
